import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import VersionHistoryItemBase, {
  VersionHistoryItemAvatar,
} from 'notebook/version-history/VersionHistoryItemBase';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {
  PublishableVersionStatus,
  PUBLISHABLE_VERSION_STATUS_TO_ICON,
  PUBLISHABLE_VERSION_STATUS_TO_COLOR,
  PUBLISHABLE_VERSION_STATUS_TO_ACTION_PAST_TENSE,
} from 'template-collectibles/publishing-ui/TemplatePublishingConstants';
import {PublishableVersionInfo} from 'template-collectibles/publishing-ui/TemplatePublishingTypes';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';
import {capitalize} from 'util/NameUtil';

export const VERSION_HISTORY_ITEM_PUBLISHING_FIRST_OPTION = 'Create new draft from version';
export const VERSION_HISTORY_ITEM_PUBLISHING_SECOND_OPTION = 'Duplicate from version';

function getTimestampRow(
  publishableVersionInfo: PublishableVersionInfo,
  versionCreator: HasAvatar,
  versionDatetime: string,
  oauth2ClientId?: string,
  oauth2ClientName?: string
): JSX.Element {
  const {publishingStatus, publishedAt, unpublishedAt, unpublishedByUser} = publishableVersionInfo;
  const isDraft = publishingStatus === PublishableVersionStatus.DRAFT;
  const timestamp = isDraft ? versionDatetime : publishedAt;

  const tooltip = (
    <div className={css(styles.usersTooltipContainer)}>
      {unpublishedAt && unpublishedByUser && (
        <div className={css(styles.timestampContainer)}>
          <span className={css(styles.timestampText)}>
            {PUBLISHABLE_VERSION_STATUS_TO_ACTION_PAST_TENSE[publishingStatus]} {readableTime(unpublishedAt)}
          </span>
          <VersionHistoryItemAvatar user={unpublishedByUser} />
        </div>
      )}
      <div className={css(styles.timestampContainer)}>
        <span className={css(styles.timestampText)}>Authored {readableTime(versionDatetime)}</span>
        <VersionHistoryItemAvatar
          user={versionCreator}
          oauth2ClientId={oauth2ClientId}
          oauth2ClientName={oauth2ClientName}
        />
      </div>
    </div>
  );

  return (
    <div className={css(styles.flexRow)}>
      <span className={css(styles.timestampText)}>
        {isDraft ? 'Authored' : 'Published'} {readableTime(timestamp)}
      </span>
      {!isDraft && (
        <Tooltip text={tooltip}>
          <BenchlingIcon style={{color: Color.GRAY_500}} icon="info-circle" />
        </Tooltip>
      )}
    </div>
  );
}

export interface VersionHistoryItemPublishingProps {
  publishableVersionInfo: PublishableVersionInfo;
  versionDatetime: string;
  versionCreator: HasAvatar;
  isSelected: boolean;
  isExpanded: boolean;
  onClick?: () => void;
  createDraftOnClick?: () => void;
  duplicateFromVersionOnClick?: () => void;
  oauth2ClientId?: string;
  oauth2ClientName?: string;
}

function VersionHistoryItemPublishing({
  publishableVersionInfo,
  versionDatetime,
  versionCreator,
  isSelected,
  isExpanded,
  onClick,
  createDraftOnClick,
  duplicateFromVersionOnClick,
  oauth2ClientId,
  oauth2ClientName,
}: VersionHistoryItemPublishingProps): JSX.Element {
  const {publishedByUser, publishingStatus, versionName, versionDescription} = publishableVersionInfo;

  if (publishingStatus === PublishableVersionStatus.DRAFT_NON_CURRENT) {
    const firstRow = (
      <span className={css(styles.versionDatetime, isSelected && styles.versionDatetimeSelected)}>
        {readableTime(versionDatetime)}
      </span>
    );
    const secondRow = (
      <VersionHistoryItemAvatar
        user={versionCreator}
        oauth2ClientId={oauth2ClientId}
        oauth2ClientName={oauth2ClientName}
      />
    );
    return (
      <VersionHistoryItemBase
        dataTestAttrs={{component: 'VersionHistoryItemPublishing', element: 'draftVersionItem'}}
        rows={[firstRow, secondRow]}
        isSelected={isSelected}
        onClick={onClick}
        firstOption={{
          text: VERSION_HISTORY_ITEM_PUBLISHING_FIRST_OPTION,
          icon: 'plus',
          onClick: createDraftOnClick,
        }}
        secondOption={{
          text: VERSION_HISTORY_ITEM_PUBLISHING_SECOND_OPTION,
          icon: 'copy',
          onClick: duplicateFromVersionOnClick,
        }}
      />
    );
  }

  const iconName = PUBLISHABLE_VERSION_STATUS_TO_ICON[publishableVersionInfo.publishingStatus];
  const iconColor = PUBLISHABLE_VERSION_STATUS_TO_COLOR[publishableVersionInfo.publishingStatus];

  const isDraft = publishingStatus === PublishableVersionStatus.DRAFT;
  const name = isDraft ? 'Draft' : versionName;
  const hasDescription = Boolean(versionDescription);
  const userToDisplay = isDraft ? versionCreator : publishedByUser!;

  const timestampRow = getTimestampRow(
    publishableVersionInfo,
    versionCreator,
    versionDatetime,
    oauth2ClientId,
    oauth2ClientName
  );
  const descriptionRow = <div className={css(styles.description)}>{versionDescription}</div>;

  const firstRow = (
    <Tooltip text={capitalize(publishingStatus)} placement={FloatingPlacement.TOP}>
      <span className={css(styles.versionNameRow)}>
        <BenchlingIcon icon={iconName} style={{color: iconColor}} />
        <span className={css(styles.versionName)}>{name}</span>
      </span>
    </Tooltip>
  );
  const secondRow = timestampRow;
  const thirdRow = (
    <VersionHistoryItemAvatar
      user={userToDisplay}
      oauth2ClientId={isDraft ? oauth2ClientId : undefined}
      oauth2ClientName={isDraft ? oauth2ClientName : undefined}
    />
  );
  const fourthRow = hasDescription && isExpanded ? descriptionRow : undefined;
  const rows = [firstRow, secondRow, thirdRow];
  if (fourthRow) {
    rows.push(fourthRow);
  }

  return (
    <VersionHistoryItemBase
      dataTestAttrs={{component: 'VersionHistoryItemPublishing', element: 'publishingVersionItem'}}
      rows={rows}
      isSelected={isSelected}
      onClick={onClick}
      firstOption={{
        text: VERSION_HISTORY_ITEM_PUBLISHING_FIRST_OPTION,
        icon: 'plus',
        onClick: createDraftOnClick,
      }}
      secondOption={{
        text: VERSION_HISTORY_ITEM_PUBLISHING_SECOND_OPTION,
        icon: 'copy',
        onClick: duplicateFromVersionOnClick,
      }}
    />
  );
}

const styles = StyleSheet.create({
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: '5px',
  },
  versionNameRow: {
    fontSize: '15px',
  },
  versionName: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Color.GRAY_900,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    overflowWrap: 'break-word',
    marginLeft: '5px',
  },
  versionDatetime: {
    fontSize: '13px',
    lineHeight: '16px',
    color: Color.GRAY_900,
  },
  versionDatetimeSelected: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  timestampText: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: Color.GRAY_600,
  },
  usersTooltipContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // We want 8px of padding surrounding the tooltip content. The tooltip itself has 8px of padding horizontally
    // and 6px of padding vertically, so we need to add 2px of padding to the top and bottom
    paddingTop: '2px',
    paddingBottom: '2px',
  },
  timestampContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  description: {
    fontSize: '13px',
    lineHeight: '16px',
    color: Color.GRAY_900,
  },
});

export default VersionHistoryItemPublishing;
